/**
 * Shared web ad renderer. One campaign, many placements: the server resolves a
 * per-placement `format` + `aspectRatio` (AdRenderModel) and this picks the matching
 * card. The SAME component renders live feed ads AND the create-flow preview, so
 * preview == production by construction. Styled with the app's navy `primary` + slate.
 */
import type { CSSProperties } from "react";

export type AdFormat = "textOnly" | "imageText" | "compact" | "large";

export interface AdRenderModel {
  id: number;
  placementKey?: string;
  format: AdFormat;
  businessName?: string | null;
  headline: string;
  subtext?: string | null;
  imageUrl?: string | null;
  ctaText: string;
  ctaType?: string;
  ctaValue?: string;
  aspectRatio?: string; // e.g. "16/9"
}

function ratioStyle(r?: string): CSSProperties {
  const [w, h] = (r ?? "16/9").split("/");
  return { aspectRatio: `${w || 16} / ${h || 9}` };
}

const Tag = ({ business }: { business?: string | null }) => (
  <div className="text-[10px] font-semibold uppercase tracking-wide text-primary">Advert{business ? ` · ${business}` : ""}</div>
);
const Cta = ({ text, onClick, size = "sm" }: { text: string; onClick?: () => void; size?: "sm" | "lg" }) => (
  <button onClick={onClick} className={`mt-2 inline-block bg-primary text-white font-medium rounded-lg ${size === "lg" ? "px-5 py-2 text-base" : "px-4 py-1.5 text-sm"}`}>{text}</button>
);

export function AdRenderer({ ad, onClick }: { ad: AdRenderModel; onClick?: () => void }) {
  const headline = <div className="font-bold text-gray-900 mt-0.5">{ad.headline}</div>;
  const sub = ad.subtext ? <div className="text-sm text-gray-600 mt-0.5">{ad.subtext}</div> : null;

  if (ad.format === "textOnly") {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-3">
        <Tag business={ad.businessName} />{headline}{sub}<Cta text={ad.ctaText} onClick={onClick} />
      </div>
    );
  }
  if (ad.format === "compact") {
    return (
      <button onClick={onClick} className="w-full flex items-center gap-3 rounded-2xl border border-slate-200 bg-white shadow-sm p-2.5 text-left">
        {ad.imageUrl && <img src={ad.imageUrl} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0" />}
        <div className="min-w-0 flex-1"><Tag business={ad.businessName} /><div className="font-semibold text-gray-900 truncate">{ad.headline}</div>{ad.subtext && <div className="text-xs text-gray-500 truncate">{ad.subtext}</div>}</div>
        <span className="text-primary text-sm font-medium shrink-0">{ad.ctaText} ›</span>
      </button>
    );
  }
  if (ad.format === "large") {
    return (
      <div className="rounded-2xl overflow-hidden shadow-sm border border-slate-200 relative bg-gradient-to-br from-[#0c1e33] to-[#1e3a5f]">
        {ad.imageUrl && <img src={ad.imageUrl} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" style={ratioStyle(ad.aspectRatio)} />}
        <div className="relative p-5" style={ratioStyle(ad.aspectRatio)}>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-white/70">Advert{ad.businessName ? ` · ${ad.businessName}` : ""}</div>
          <div className="font-bold text-white text-2xl mt-1 max-w-md">{ad.headline}</div>
          {ad.subtext && <div className="text-white/80 mt-1 max-w-md">{ad.subtext}</div>}
          <Cta text={ad.ctaText} onClick={onClick} size="lg" />
        </div>
      </div>
    );
  }
  // imageText (default)
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
      {ad.imageUrl && <img src={ad.imageUrl} alt="" className="w-full object-cover" style={ratioStyle(ad.aspectRatio)} />}
      <div className="p-3"><Tag business={ad.businessName} />{headline}{sub}<Cta text={ad.ctaText} onClick={onClick} /></div>
    </div>
  );
}
